import Intercom, { hide, shutdown, showNewMessage, update } from '@intercom/messenger-js-sdk';
import { getKeycloak } from './auth/keycloak-instance';
import { candidateEnv } from './config/env';

/** Support mailbox used as the fallback when the Intercom messenger is off. */
const SUPPORT_EMAIL = 'suporte@eureca.me';

/**
 * Intercom Messenger — réplica do uso do `@intercom/messenger-js-sdk` no
 * frontend legado (`candidate-web` / `auth.eureca.me`):
 *
 *   - legado `auth.eureca.me/main.tsx`        → `Intercom({ app_id })` anônimo.
 *   - legado `candidate-web` `auth-guard`     → `Intercom({ app_id, user_id, name, email })`
 *                                               quando logado; anônimo caso contrário.
 *   - legado `candidate-web` account-dropdown → `hide()` + `shutdown()` no logout.
 *
 * O `app_id` (id público do workspace Intercom) vem de `VITE_INTERCOM_APP_ID`,
 * seguindo a convenção dos containers GTM: setado por ambiente no workflow de
 * deploy; vazio/ausente → boot vira no-op (messenger desabilitada). @see gtm.ts
 *
 * Bootar a messenger faz `window.Intercom` existir, o que ATIVA as chamadas
 * `window.Intercom('show')` já espalhadas pelas telas de inscrição
 * (`features/subscription/**`) — antes deste módulo elas eram no-ops.
 */

type IntercomUser = { user_id?: string; name?: string; email?: string };

/**
 * Lê a identidade do candidato do token Keycloak. O `user_id` da Intercom usa o
 * `sub` (subject do Keycloak) — o identificador canônico de participante usado
 * no resto do SPA (mesmo `participantId` do Mixpanel); o realm candidate não
 * emite claim `user_id` dedicado. `name`/`email` vêm dos scopes padrão do KC
 * (presentes no access token). Devolve `{}` quando deslogado ou antes do
 * Keycloak resolver → boot anônimo.
 */
function resolveIntercomUser(): IntercomUser {
  try {
    const kc = getKeycloak();
    const t = kc.tokenParsed as Record<string, unknown> | undefined;
    if (!kc.authenticated || !t) return {};
    const userId = typeof t.sub === 'string' && t.sub ? t.sub : undefined;
    const name = typeof t.name === 'string' ? t.name : undefined;
    const email = typeof t.email === 'string' ? t.email : undefined;
    return {
      ...(userId ? { user_id: userId } : {}),
      ...(name ? { name } : {}),
      ...(email ? { email } : {}),
    };
  } catch {
    // Antes do Keycloak inicializar — trata como anônimo.
    return {};
  }
}

/**
 * Inicializa a Intercom Messenger uma única vez, com a identidade resolvida do
 * estado de auth atual. Deve ser chamado depois que o Keycloak resolve
 * (ver `KeycloakGate`). No-op quando `VITE_INTERCOM_APP_ID` não está setado.
 *
 * O SDK só inicializa de fato na PRIMEIRA chamada de `Intercom()` (guard de
 * singleton interno) — por isso passamos a identidade final aqui, em vez de
 * bootar anônimo e depois "identificar" (a segunda chamada seria no-op). Como
 * o login sempre faz full-reload (`keycloak.login()` redireciona), uma única
 * init por carregamento de página já reflete o estado correto.
 */
export function bootIntercom(): void {
  const appId = candidateEnv.intercomAppId?.trim();
  if (!appId) return;
  Intercom({ app_id: appId, ...resolveIntercomUser() });
}

/**
 * EU-534 — abre o chat de suporte com uma mensagem pré-preenchida ("Falar com o
 * suporte"). Com a messenger habilitada (`VITE_INTERCOM_APP_ID`) usa
 * `showNewMessage` (o chat já sobe identificado pelo boot no `KeycloakGate`, com
 * `user_id = sub`); sem `app_id` (messenger desabilitada) cai para um `mailto`
 * de suporte, para o candidato nunca ficar sem saída.
 *
 * Privacidade: a mensagem deve citar apenas o número que o PRÓPRIO candidato
 * está tentando usar — nunca a conta/e-mail do outro titular.
 */
export function showSupportMessage(message: string): void {
  if (candidateEnv.intercomAppId?.trim()) {
    showNewMessage(message);
    return;
  }
  window.location.href = `mailto:${SUPPORT_EMAIL}?body=${encodeURIComponent(message)}`;
}

/**
 * Encerra a sessão da Intercom no logout (réplica do `hide()` + `shutdown()`
 * do account-dropdown legado). Limpa cookies/local storage da Intercom antes
 * do redirect de logout do Keycloak, evitando que um próximo usuário no mesmo
 * navegador veja a sessão Intercom anterior. No-op quando a messenger não está
 * habilitada (`VITE_INTERCOM_APP_ID` ausente).
 */
export function shutdownIntercom(): void {
  if (!candidateEnv.intercomAppId?.trim()) return;
  hide();
  shutdown();
}

/**
 * EU-761/762 (bug da QA 25/09) — o launcher flutuante ("eu") cobria o botão
 * primário do footer, o "Fechar" do Sonner e o "Voltar" dos Drawers no fluxo
 * do currículo (RN-46: alvo de toque livre). O fluxo esconde o launcher e o
 * devolve ao sair; a ajuda segue no "?" do header. No-op sem messenger.
 */
export function setIntercomLauncherHidden(hidden: boolean): void {
  if (!candidateEnv.intercomAppId?.trim()) return;
  try {
    update({ hide_default_launcher: hidden });
  } catch {
    // Messenger ainda não bootou (ou foi desligada): nada a esconder.
  }
}
